import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Heart, Bookmark, Trash2, BedDouble, Bath } from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { formatPrice, img } from "../../lib/utils-app";
import { toast } from "sonner";

export function Favorites() {
  const { t, L } = useLang();
  const [items, setItems] = useState(null);

  const load = () => api.get("/favorites").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const remove = async (id) => { await api.delete(`/favorites/${id}`); toast.success("Removed"); load(); };

  if (items === null) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div>
      <h1 className="font-serif text-4xl font-light mb-2">{t("dash.favorites")}</h1>
      <p className="text-muted-foreground font-light mb-8">{items.length} saved</p>
      {items.length === 0 ? (
        <div className="py-20 text-center" data-testid="no-favorites">
          <Heart size={40} className="text-muted-foreground mx-auto mb-4" strokeWidth={1} />
          <p className="text-muted-foreground font-light">{t("dash.noFavorites")}</p>
          <Link to="/buy" className="inline-block mt-5 px-6 py-3 bg-primary text-primary-foreground label-caps">{t("nav.buy")}</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => (
            <div key={p.id} className="flex gap-4 bg-card border border-border p-3" data-testid={`fav-${p.id}`}>
              <Link to={`/property/${p.id}`} className="w-32 h-28 flex-shrink-0 overflow-hidden"><img src={img(p.images?.[0])} alt="" className="w-full h-full object-cover" /></Link>
              <div className="flex-1 min-w-0 py-1">
                <Link to={`/property/${p.id}`}><h3 className="font-serif text-xl truncate hover:text-primary transition-colors">{L(p.title)}</h3></Link>
                <p className="text-primary font-serif text-lg">{formatPrice(p.price, p.currency)}</p>
                <p className="text-xs text-muted-foreground truncate">{p.neighborhood}, {p.city}</p>
                <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                  {p.bedrooms > 0 && <span className="flex items-center gap-1"><BedDouble size={12} />{p.bedrooms}</span>}
                  {p.bathrooms > 0 && <span className="flex items-center gap-1"><Bath size={12} />{p.bathrooms}</span>}
                </div>
              </div>
              <button onClick={() => remove(p.id)} className="p-2 text-muted-foreground hover:text-destructive transition-colors self-start" data-testid={`remove-fav-${p.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SavedSearches() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");

  const load = () => api.get("/saved-searches").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name) return;
    await api.post("/saved-searches", { name, filters: {}, alerts: true });
    toast.success("Search saved");
    setName(""); load();
  };
  const remove = async (id) => { await api.delete(`/saved-searches/${id}`); load(); };

  if (items === null) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div>
      <h1 className="font-serif text-4xl font-light mb-2">{t("dash.savedSearches")}</h1>
      <p className="text-muted-foreground font-light mb-8">Get alerts when new matches appear.</p>

      <form onSubmit={add} className="flex gap-2 mb-8 max-w-md">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Waterfront under $5M" className="flex-1 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors" data-testid="saved-search-name" />
        <button className="px-5 bg-primary text-primary-foreground label-caps" data-testid="add-saved-search">{t("common.save")}</button>
      </form>

      {items.length === 0 ? (
        <div className="py-16 text-center"><Bookmark size={40} className="text-muted-foreground mx-auto mb-4" strokeWidth={1} /><p className="text-muted-foreground font-light">No saved searches yet.</p></div>
      ) : (
        <div className="space-y-3 max-w-2xl">
          {items.map((s) => (
            <div key={s.id} className="flex items-center gap-3 bg-card border border-border p-4" data-testid={`saved-${s.id}`}>
              <Bookmark size={18} className="text-primary" />
              <div className="flex-1"><p className="font-light">{s.name}</p>{s.alerts && <p className="text-xs text-emerald-400">Alerts on</p>}</div>
              <button onClick={() => remove(s.id)} className="p-2 text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-saved-${s.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
